if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          this.initQuickLightbox();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }

    /* === Quick-add image lightbox ===
     * Since we remove the original product-modal (to avoid ID conflicts with main page),
     * we add our own simple fullscreen lightbox for product images inside quick-add.
     */
    initQuickLightbox() {
      // Collect all product images
      const mediaItems = this.modalContent.querySelectorAll('.product__media img');
      if (!mediaItems.length) return;

      const images = [];
      mediaItems.forEach(img => {
        // Get the highest resolution src
        const src = img.src?.replace(/width=\d+/, 'width=1800') 
                    || img.currentSrc?.replace(/width=\d+/, 'width=1800');
        if (src) images.push({ src, alt: img.alt || '' });
      });
      if (!images.length) return;

      // Make media items clickable
      mediaItems.forEach((img, index) => {
        const wrapper = img.closest('.product__media-item') || img.closest('.product__media') || img.parentElement;
        wrapper.style.cursor = 'zoom-in';
        wrapper.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openLightbox(images, index);
        });
      });

      // Also hook up any existing zoom/modal-opener buttons
      const zoomBtns = this.modalContent.querySelectorAll('.product__modal-opener, [data-modal]');
      zoomBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Find closest media item index
          const slide = btn.closest('[id^="Slide-"]') || btn.closest('.product__media-item');
          const idx = slide ? Array.from(slide.parentElement.children).indexOf(slide) : 0;
          this.openLightbox(images, Math.min(idx, images.length - 1));
        });
      });
    }

    openLightbox(images, startIndex = 0) {
      // Remove existing lightbox if any
      const existing = document.querySelector('.qa-lightbox');
      if (existing) existing.remove();

      let currentIndex = startIndex;

      const overlay = document.createElement('div');
      overlay.className = 'qa-lightbox';
      overlay.innerHTML = `
        <style>
          .qa-lightbox {
            position: fixed; inset: 0; z-index: 99999;
            background: rgba(0,0,0,0.92); backdrop-filter: blur(8px);
            display: flex; align-items: center; justify-content: center;
            animation: qaFadeIn 0.2s ease;
          }
          @keyframes qaFadeIn { from { opacity: 0; } to { opacity: 1; } }
          .qa-lightbox__img {
            max-width: 90vw; max-height: 90vh;
            object-fit: contain; border-radius: 4px;
            box-shadow: 0 8px 40px rgba(0,0,0,0.4);
          }
          .qa-lightbox__close {
            position: absolute; top: 16px; right: 16px;
            width: 44px; height: 44px; border-radius: 50%;
            background: rgba(255,255,255,0.12); border: none;
            color: #fff; font-size: 24px; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: background 0.2s;
          }
          .qa-lightbox__close:hover { background: rgba(255,255,255,0.25); }
          .qa-lightbox__nav {
            position: absolute; top: 50%; transform: translateY(-50%);
            width: 44px; height: 44px; border-radius: 50%;
            background: rgba(255,255,255,0.12); border: none;
            color: #fff; font-size: 22px; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: background 0.2s;
          }
          .qa-lightbox__nav:hover { background: rgba(255,255,255,0.25); }
          .qa-lightbox__prev { left: 16px; }
          .qa-lightbox__next { right: 16px; }
          .qa-lightbox__counter {
            position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%);
            color: rgba(255,255,255,0.6); font-size: 14px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
        </style>
        <button class="qa-lightbox__close" aria-label="Stäng">✕</button>
        ${images.length > 1 ? `
          <button class="qa-lightbox__nav qa-lightbox__prev" aria-label="Föregående">‹</button>
          <button class="qa-lightbox__nav qa-lightbox__next" aria-label="Nästa">›</button>
          <div class="qa-lightbox__counter">${currentIndex + 1} / ${images.length}</div>
        ` : ''}
        <img class="qa-lightbox__img" src="${images[currentIndex].src}" alt="${images[currentIndex].alt}">
      `;

      document.body.appendChild(overlay);

      const imgEl = overlay.querySelector('.qa-lightbox__img');
      const counter = overlay.querySelector('.qa-lightbox__counter');

      const updateImage = () => {
        imgEl.src = images[currentIndex].src;
        imgEl.alt = images[currentIndex].alt;
        if (counter) counter.textContent = `${currentIndex + 1} / ${images.length}`;
      };

      overlay.querySelector('.qa-lightbox__close').addEventListener('click', () => overlay.remove());
      overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

      const prevBtn = overlay.querySelector('.qa-lightbox__prev');
      const nextBtn = overlay.querySelector('.qa-lightbox__next');
      if (prevBtn) prevBtn.addEventListener('click', () => { currentIndex = (currentIndex - 1 + images.length) % images.length; updateImage(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { currentIndex = (currentIndex + 1) % images.length; updateImage(); });

      document.addEventListener('keydown', function handler(e) {
        if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', handler); }
        if (e.key === 'ArrowLeft' && prevBtn) { currentIndex = (currentIndex - 1 + images.length) % images.length; updateImage(); }
        if (e.key === 'ArrowRight' && nextBtn) { currentIndex = (currentIndex + 1) % images.length; updateImage(); }
      });
    }
  });
}
